/**
 * Forschung, KI-Assistent und Einstellungen (Spielstände, Import/Export).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ResearchNode } from '@hosting-sim/core';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Meter,
  Modal,
  Notice,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  StatusDot,
  Tabs,
} from '../components/ui';
import { useCanControlTime, useGame, useMetrics } from '../store/game';
import { useAreaVisibility } from '../store/auth';
import { MembersPanel } from '../components/GameMembers';
import { api } from '../lib/api';
import { duration, gameDate, money, num, pct100 } from '../lib/format';

// ---------------------------------------------------------------------------
// Forschung
// ---------------------------------------------------------------------------

const CATEGORY_LABEL: Record<ResearchNode['category'], string> = {
  network: 'Netzwerk',
  compute: 'Rechenleistung',
  efficiency: 'Effizienz',
  automation: 'Automatisierung',
  security: 'Sicherheit',
  finance: 'Finanzen',
};

const MODIFIER_LABEL: Record<string, { label: string; better: 'higher' | 'lower' }> = {
  powerUsage: { label: 'Stromverbrauch', better: 'lower' },
  coolingEfficiency: { label: 'Kühleffizienz', better: 'higher' },
  computePerformance: { label: 'Rechenleistung', better: 'higher' },
  routingCapacity: { label: 'Routingkapazität', better: 'higher' },
  staffEfficiency: { label: 'Personalbedarf', better: 'lower' },
  failureRate: { label: 'Ausfallrate', better: 'lower' },
  ddosMitigationGbps: { label: 'DDoS-Abwehr (Gbit/s)', better: 'higher' },
  supportSpeed: { label: 'Supportgeschwindigkeit', better: 'higher' },
  loanRateMultiplier: { label: 'Kreditzinsen', better: 'lower' },
};

export function Research() {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [category, setCategory] = useState('all');

  if (!world) return null;
  const nodes = Object.values(world.research);
  const filtered = category === 'all' ? nodes : nodes.filter((n) => n.category === category);
  const active = world.activeResearchId ? world.research[world.activeResearchId] : null;
  const completed = nodes.filter((n) => n.completed);

  return (
    <>
      <PageHeader title="Forschung" description="Technologien schalten Hardware frei und verbessern den laufenden Betrieb." />

      <StatGrid cols={3}>
        <Stat label="Abgeschlossen" value={`${completed.length} / ${nodes.length}`} />
        <Stat
          label="Laufendes Projekt"
          value={active ? active.name : 'keines'}
          hint={active ? `noch ${duration(active.durationTicks - active.progressTicks)}` : 'Ein Projekt auswählen'}
        />
        <Stat
          label="Investition gesamt"
          value={money(completed.reduce((s, n) => s + n.cost, 0))}
          hint="in abgeschlossene Forschung"
        />
      </StatGrid>

      {active && (
        <Card className="mt-4" title={`Laufend: ${active.name}`}>
          <Meter
            value={active.progressTicks}
            max={active.durationTicks}
            label={
              <>
                <span>{active.description}</span>
                <span className="tabular">{pct100((active.progressTicks / active.durationTicks) * 100, 0)}</span>
              </>
            }
          />
          <p className="mt-2 text-xs text-panel-500 dark:text-panel-400">
            Die Kosten von {money(active.cost)} werden gleichmäßig über die Laufzeit verbucht.
          </p>
        </Card>
      )}

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'all', label: 'Alle' },
            ...Object.entries(CATEGORY_LABEL).map(([id, label]) => ({ id, label })),
          ]}
          active={category}
          onChange={setCategory}
        />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((node) => {
          const unlocked = node.requires.every((id) => world.research[id]?.completed);
          const blockers = node.requires.filter((id) => !world.research[id]?.completed).map((id) => world.research[id]?.name ?? id);

          return (
            <Card
              key={node.id}
              title={node.name}
              subtitle={CATEGORY_LABEL[node.category]}
              actions={
                node.completed ? (
                  <Badge tone="success">erforscht</Badge>
                ) : world.activeResearchId === node.id ? (
                  <Badge tone="info">läuft</Badge>
                ) : (
                  <Badge tone="muted">{money(node.cost)}</Badge>
                )
              }
            >
              <p className="mb-3 text-xs text-panel-600 dark:text-panel-300">{node.description}</p>

              {Object.entries(node.modifiers).length > 0 && (
                <ul className="mb-3 space-y-0.5">
                  {Object.entries(node.modifiers).map(([key, value]) => {
                    const meta = MODIFIER_LABEL[key];
                    if (!meta || value === undefined) return null;
                    const isAdditive = key === 'ddosMitigationGbps';
                    const change = isAdditive ? `+${value}` : `${value > 1 ? '+' : ''}${Math.round((value - 1) * 100)} %`;
                    const good = isAdditive || (meta.better === 'higher' ? value > 1 : value < 1);
                    return (
                      <li key={key} className="flex justify-between text-2xs">
                        <span className="text-panel-500 dark:text-panel-400">{meta.label}</span>
                        <span className={good ? 'text-accent-600 dark:text-accent-400' : 'text-red-600 dark:text-red-400'}>{change}</span>
                      </li>
                    );
                  })}
                </ul>
              )}

              {node.unlocksModels.length > 0 && (
                <p className="mb-3 text-2xs text-panel-500 dark:text-panel-400">Schaltet frei: {node.unlocksModels.join(', ')}</p>
              )}
              {node.creditLimitBonus > 0 && (
                <p className="mb-3 text-2xs text-accent-600 dark:text-accent-400">Kreditlinie bei Abschluss: +{money(node.creditLimitBonus)}</p>
              )}

              {!node.completed && (
                <>
                  {!unlocked && <Notice tone="warning">Voraussetzung: {blockers.join(', ')}</Notice>}
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-2 w-full"
                    disabled={!unlocked || world.activeResearchId === node.id}
                    onClick={() => void run({ type: 'research/start', researchId: node.id })}
                  >
                    {world.activeResearchId === node.id ? 'läuft bereits' : `Starten – ${Math.round(node.durationTicks / 24)} Tage`}
                  </Button>
                </>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// KI-Assistent
// ---------------------------------------------------------------------------

const TOPICS = [
  { id: 'routing', label: 'Routinganalyse' },
  { id: 'costs', label: 'Kostenoptimierung' },
  { id: 'capacity', label: 'Kapazitätsplanung' },
  { id: 'reliability', label: 'Ausfallsicherheit' },
];

export function Advisor() {
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  const [topic, setTopic] = useState('routing');
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.advisor>> | null>(null);
  const [area, setArea] = useState('all');

  useEffect(() => {
    if (!gameId) return;
    void api.advisor(gameId, topic).then(setResult).catch(() => setResult(null));
  }, [gameId, topic, metrics?.meta.tick]);

  const alerts = result?.alerts ?? metrics?.derived.alerts ?? [];
  const filtered = area === 'all' ? alerts : alerts.filter((a) => severityGroup(a.severity) === area);

  return (
    <>
      <PageHeader
        title="KI-Assistent"
        description="Automatische Analyse von Netz, Kosten, Kapazität und Betriebsrisiken."
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Analyse" className="lg:col-span-2">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {TOPICS.map((t) => (
              <Button key={t.id} size="sm" variant={topic === t.id ? 'primary' : 'secondary'} onClick={() => setTopic(t.id)}>
                {t.label}
              </Button>
            ))}
          </div>

          {result?.analysis ? (
            <div className="space-y-3">
              <div className="surface-muted p-3">
                <p className="text-xs font-medium text-panel-500 dark:text-panel-400">{result.analysis.question}</p>
                <p className="mt-1 text-sm">{result.analysis.answer}</p>
              </div>
              {result.analysis.details.length > 0 && (
                <ul className="space-y-1">
                  {result.analysis.details.slice(0, 25).map((detail, index) => (
                    <li key={index} className="text-xs text-panel-600 dark:text-panel-300">
                      · {detail}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="py-6 text-center text-xs text-panel-500">Analyse wird erstellt …</p>
          )}
        </Card>

        <Card title="Zusammenfassung">
          {metrics && (
            <div className="space-y-2.5">
              <Field label="Erreichbarkeit">
                <StatusDot
                  tone={metrics.derived.internetReachability > 0.98 ? 'success' : metrics.derived.internetReachability > 0.5 ? 'warning' : 'danger'}
                  label={pct100(metrics.derived.internetReachability * 100, 1)}
                />
              </Field>
              <Field label="Transitanteil">{pct100(metrics.derived.traffic.transitShare * 100, 0)}</Field>
              <Field label="Monatsergebnis">{money(metrics.derived.finance.monthlyProfit)}</Field>
              <Field label="Zufriedenheit">{Math.round(metrics.derived.customers.avgSatisfaction)} / 100</Field>
              <Field label="Kritische Befunde">{alerts.filter((a) => a.severity === 'critical').length}</Field>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'all', label: 'Alle Befunde', badge: <Badge tone="muted">{alerts.length}</Badge> },
            { id: 'critical', label: 'Kritisch', badge: <Badge tone="danger">{alerts.filter((a) => severityGroup(a.severity) === 'critical').length}</Badge> },
            { id: 'warning', label: 'Warnungen' },
            { id: 'info', label: 'Hinweise' },
          ]}
          active={area}
          onChange={setArea}
        />
      </div>

      <div className="mt-4 space-y-2">
        {filtered.length === 0 && <EmptyState title="Keine Befunde" description="In dieser Kategorie ist derzeit alles unauffällig." />}
        {filtered.map((alert) => (
          <Notice
            key={alert.id}
            tone={alert.severity === 'critical' ? 'danger' : alert.severity === 'error' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}
            title={alert.title}
          >
            <p>{alert.message}</p>
            {alert.suggestion && <p className="mt-1 text-xs italic">Empfehlung: {alert.suggestion}</p>}
          </Notice>
        ))}
      </div>
    </>
  );
}

function severityGroup(severity: string): string {
  if (severity === 'critical' || severity === 'error') return 'critical';
  if (severity === 'warning') return 'warning';
  return 'info';
}

// ---------------------------------------------------------------------------
// Einstellungen
// ---------------------------------------------------------------------------

export function Settings() {
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  const toast = useGame((s) => s.toast);
  const refreshWorld = useGame((s) => s.refreshWorld);
  const setRunning = useGame((s) => s.setRunning);
  const showAllAreas = useAreaVisibility((s) => s.showAll);
  const toggleAreas = useAreaVisibility((s) => s.toggle);
  const navigate = useNavigate();
  const canControlTime = useCanControlTime();

  const [saves, setSaves] = useState<{ slot: number; label: string; tick: number; gameDate: string; cash: number; customers: number; updatedAt: string }[]>([]);
  const [slot, setSlot] = useState('1');
  const [label, setLabel] = useState('Zwischenstand');
  const [busy, setBusy] = useState(false);

  const reloadSaves = async () => {
    if (!gameId) return;
    const games = await api.listGames();
    setSaves(games.find((g) => g.id === gameId)?.saves ?? []);
  };

  useEffect(() => {
    void reloadSaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  if (!metrics) return null;

  const exportSave = async () => {
    if (!gameId) return;
    const snapshot = await api.exportSave(gameId);
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${metrics.company.name.toLowerCase().replace(/\W+/g, '-')}-tick-${metrics.meta.tick}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast('success', 'Spielstand wurde heruntergeladen.');
  };

  const importSave = async (file: File) => {
    if (!gameId) return;
    setBusy(true);
    try {
      const snapshot = JSON.parse(await file.text()) as Record<string, unknown>;
      await api.importSave(gameId, snapshot);
      await refreshWorld();
      await reloadSaves();
      toast('success', 'Spielstand übernommen.');
    } catch (error) {
      toast('error', (error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Einstellungen" description="Spielstände, Import und Export sowie Angaben zur Partie." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Partie">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Unternehmen">{metrics.company.name}</Field>
            <Field label="Autonomes System">AS{metrics.company.asn}</Field>
            <Field label="Spielstand">{metrics.meta.name}</Field>
            <Field label="Schwierigkeit">{metrics.meta.difficulty}</Field>
            <Field label="Spieldatum">{gameDate(metrics.meta.date)}</Field>
            <Field label="Tick">{num(metrics.meta.tick)}</Field>
            <Field label="Startwert">{num(metrics.meta.seed)}</Field>
            <Field label="Schemaversion">{metrics.meta.version}</Field>
          </div>

          {/* Die Seitenleiste wächst mit dem Fortschritt. Wer das Spiel kennt,
              schaltet hier einmal um und hat nie wieder damit zu tun. */}
          <div className="mt-4 flex items-start justify-between gap-3 border-t divider pt-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">Alle Bereiche anzeigen</p>
              <p className="mt-0.5 text-xs text-panel-500 dark:text-panel-400">
                Normalerweise erscheinen Bereiche wie BGP oder IP-Adressen erst, wenn die Kampagne sie braucht.
                Hier lassen sich alle sofort einblenden.
              </p>
            </div>
            <Button variant={showAllAreas ? 'primary' : 'ghost'} onClick={toggleAreas} className="shrink-0">
              {showAllAreas ? 'Alle sichtbar' : 'Mitwachsend'}
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 border-t divider pt-3">
            <Button disabled={!canControlTime} onClick={() => void setRunning(true, 1)}>
              Simulation starten
            </Button>
            <Button variant="ghost" disabled={!canControlTime} onClick={() => void setRunning(false)}>
              Anhalten
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              Zur Partienübersicht
            </Button>
          </div>
          {!canControlTime && (
            <p className="mt-2 text-xs text-panel-500 dark:text-panel-400">
              Nur Administratoren und Techniker steuern die Zeit.
            </p>
          )}
        </Card>

        <Card title="Sichern und Laden">
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <Select label="Platz" value={slot} onChange={(e) => setSlot(e.target.value)} className="w-24" disabled={!canControlTime}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
              <Input
                label="Bezeichnung"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="flex-1"
                disabled={!canControlTime}
              />
              <Button
                variant="primary"
                loading={busy}
                disabled={!canControlTime}
                onClick={async () => {
                  if (!gameId) return;
                  setBusy(true);
                  try {
                    await api.save(gameId, Number(slot), label);
                    await reloadSaves();
                    toast('success', `Spielstand in Platz ${slot} gesichert.`);
                  } catch (error) {
                    toast('error', (error as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Sichern
              </Button>
            </div>

            <div className="space-y-1.5">
              {saves.map((save) => (
                <div key={save.slot} className="surface-muted flex flex-wrap items-center gap-2 p-2.5 text-xs">
                  <Badge tone={save.slot === 0 ? 'info' : 'neutral'}>{save.slot === 0 ? 'Auto' : save.slot}</Badge>
                  <span className="min-w-0 flex-1 truncate font-medium">{save.label}</span>
                  <span className="tabular text-panel-500">{gameDate(save.gameDate).split(',')[0]}</span>
                  <span className="tabular text-panel-500">{money(save.cash, true)}</span>
                  <span className="tabular text-panel-500">{num(save.customers)} Kunden</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!canControlTime}
                    title={canControlTime ? undefined : 'Nur Administratoren und Techniker laden Spielstände.'}
                    onClick={async () => {
                      if (!gameId) return;
                      if (!confirm(`Spielstand "${save.label}" laden? Der aktuelle Fortschritt geht verloren.`)) return;
                      await api.load(gameId, save.slot);
                      await refreshWorld();
                      toast('success', 'Spielstand geladen.');
                    }}
                  >
                    laden
                  </Button>
                </div>
              ))}
              {saves.length === 0 && <p className="text-xs text-panel-500">Noch keine Spielstände gesichert.</p>}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t divider pt-3">
              <Button onClick={exportSave}>Als Datei exportieren</Button>
              <label className="inline-flex">
                <span className="sr-only">Spielstand importieren</span>
                <input
                  type="file"
                  accept="application/json"
                  className="block w-full text-xs file:mr-2 file:cursor-pointer file:rounded-md file:border file:border-panel-300 file:bg-white file:px-3 file:py-1.5 file:text-xs file:font-medium dark:file:border-panel-700 dark:file:bg-panel-800 dark:file:text-panel-100"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void importSave(file);
                  }}
                />
              </label>
            </div>

            <Notice tone="info">
              Der Export enthält den vollständigen Weltzustand. Er lässt sich in jede Partie desselben Spielstands-Schemas einspielen und
              eignet sich zum Teilen oder Sichern vor riskanten Umbauten.
            </Notice>
          </div>
        </Card>

        <Card title="Mitspieler" className="lg:col-span-2">
          {gameId && <MembersPanel gameId={gameId} />}
        </Card>
      </div>
    </>
  );
}
